import React from 'react';
import {
    Container,
    Paper,
    Button,
    Typography,
    Box,
    styled,
    alpha,
    Divider,
    useTheme,
} from '@mui/material';
import {
    Description,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const BackgroundContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: theme.palette.mode === 'dark'
        ? `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.04) 0%, transparent 50%),
      linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)
    `
        : `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)
    `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: theme.shadows[8],
    backdropFilter: 'blur(10px)',
    background: alpha(theme.palette.background.paper, 0.9),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    maxWidth: 800,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5),
    fontSize: '1rem',
}));

const DPAPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const handleBack = () => {
        // Se veio do SignUpForm, volta para o login com a aba de cadastro ativa e os dados
        if (location.state?.fromSignUp) {
            navigate('/login', { 
                state: { 
                    showSignUp: true,
                    formData: location.state.formData
                } 
            });
        } else {
            // Caso contrário, volta para a página anterior
            navigate(-1);
        }
    };

    return (
        <BackgroundContainer>
            <Container maxWidth="md">
                <StyledPaper elevation={3}>
                    <Box mb={3}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Description sx={{ mr: 2, color: 'primary.main' }} />
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                Acordo de Processamento de Dados Pessoais (DPA)
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ '& > *': { mb: 3 } }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                O presente Acordo para Tratamento de Dados Pessoais ("DPA") é celebrado entre a CONTRATANTE, devidamente qualificada no Contrato de Prestação de Serviços e/ou Ordem de Serviço, doravante denominada "CONTRATANTE", e LIATALK AI LTDA, sociedade limitada, com sede na cidade de São Paulo, Estado de São Paulo, na Av. Paulista, 807, Conj. 1614 – Bela Vista – CEP 01311-915, neste ato representada na forma de seu Estatuto Social, doravante denominada simplesmente CONTRATADA.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                CONTRATANTE e CONTRATADA serão doravante denominadas, em conjunto, simplesmente "PARTE(S)".
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Considerando que:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">I. As partes celebraram um contrato de prestação de serviços e/ou ordem de serviço para que a CONTRATADA forneça à CONTRATANTE ambiente no-code/low-code para configuração de agentes de IA e integrações com serviços de terceiros;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">II. Dentro do escopo dessa relação, a CONTRATADA poderá tratar dados pessoais pertencentes à CONTRATANTE oriundos da solução acordada;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">III. A Lei Geral de Proteção de Dados ("LGPD") estabelece regras e diretrizes que deverão nortear o tratamento de dados pessoais;</Typography></li>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Este Acordo de Processamento de Dados (DPA) regula o tratamento de dados pessoais realizado pela LIATALK AI LTDA ("LIATALK AI"), na qualidade de OPERADORA, em nome do Cliente (CONTROLADOR), em decorrência do uso e contratação dos serviços digitais disponibilizados na Plataforma LIATALK AI ("PLATAFORMA"), ambiente low-code/no-code (LCNC) destinado à configuração de agentes de Inteligência Artificial e integrações com serviços de terceiros.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                As partes firmam o presente DPA a fim de estabelecer os requisitos mínimos de proteção de dados que deverão ser observados em conexão com o desempenho das atividades descritas no presente instrumento ou na medida em que, de outra forma, tratem DADOS PESSOAIS no âmbito das relações contratuais.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Salvo disposição em contrário posteriormente, no caso de qualquer conflito, os termos deste DPA prevalecerão.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA PRIMEIRA - OBJETO</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                1.1. Constitui objeto deste DPA o tratamento de dados e/ou armazenamento de toda e qualquer informação e/ou dados pessoais relacionados à contratação dos serviços prestados pela CONTRATADA, em especial os destinados à configuração de agentes de Inteligência Artificial e integrações com serviços de terceiros, por meio da PLATAFORMA LIATALK AI.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA SEGUNDA - DEFINIÇÕES</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                2.1. Neste DPA, as expressões em letra maiúscula, no singular ou plural, terão os significados especificados abaixo:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">(a) LGPD: Lei Geral de Proteção de Dados Pessoais (Lei Federal nº 13.709/18);</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(b) CONTROLADOR(A): quem define as finalidades e os meios do tratamento;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(c) OPERADOR(A): quem realiza o tratamento em nome do Controlador;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(d) SUBOPERADOR(A): quem auxilia o Operador a realizar o tratamento;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(e) TITULAR: pessoa natural a quem se referem os dados pessoais;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(f) DADOS PESSOAIS: qualquer informação relacionada a pessoa física identificada ou identificável;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(g) DADOS PESSOAIS SENSÍVEIS: dados sobre origem racial, religiosa, política, saúde, vida sexual, genética ou biométrica;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(h) TRATAMENTO: qualquer operação realizada com dados pessoais;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(i) VIOLAÇÃO: incidente de segurança com perda, acesso ou divulgação indevida de dados pessoais;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(j) ANPD: Autoridade Nacional de Proteção de Dados;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(k) LOW-CODE: método de desenvolvimento de aplicações com pouca codificação;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(l) NO-CODE: método de desenvolvimento que dispensa codificação;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(m) PLATAFORMA: ferramenta LCNC que permite criar e automatizar fluxos de trabalho e aplicações.</Typography></li>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA TERCEIRA - AGENTES DO TRATAMENTO</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                3.1. A CONTRATANTE figurará como CONTROLADORA, e a CONTRATADA, LIATALK AI LTDA, como OPERADORA de todos os dados pessoais tratados no âmbito deste DPA.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                3.2. A CONTRATADA poderá utilizar suboperadores (como provedores de nuvem, e-mail, análise e IA), comprometendo-se a selecionar apenas fornecedores que observem padrões de segurança e confidencialidade equivalentes aos previstos neste Acordo.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA QUARTA - CONFORMIDADE COM A LEGISLAÇÃO DE PROTEÇÃO DE DADOS</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                4.1. As partes deverão estar em conformidade com a legislação de proteção de dados, especialmente a LGPD e as normas complementares.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA QUINTA - TERMOS DO TRATAMENTO</Typography>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">5.1. Obrigações da CONTRATADA:</Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">(a) Tratar os dados pessoais apenas para fins de prestação dos serviços;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(b) Informar à CONTRATANTE caso qualquer instrução viole a legislação aplicável;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(c) Garantir confidencialidade a todos os envolvidos no tratamento;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(d) Gerenciar acessos e manter rastreabilidade;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(e) Implementar medidas técnicas e administrativas de segurança;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(f) Assegurar conformidade de seus suboperadores;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(g) Não transferir dados sem autorização;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(h) Responder por atos ou omissões próprios e de seus suboperadores;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(i) Notificar a CONTRATANTE sobre solicitações de titulares ou da ANPD;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(j) Fornecer suporte técnico e documental;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(k) Manter registros e informações necessários ao cumprimento das obrigações legais;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(l) Realizar o tratamento apenas nos locais acordados.</Typography></li>
                            </Box>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">5.2. Obrigações da CONTRATANTE:</Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">(a) Fornecer instruções claras à CONTRATADA;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(b) Garantir acesso seguro e monitorado aos dados;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(c) Ser responsável pela precisão e legalidade dos dados fornecidos;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(d) Garantir que o compartilhamento de dados esteja em conformidade com a LGPD;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(e) Informar os titulares e garantir seus direitos;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(f) Evitar o compartilhamento de dados ilícitos ou obtidos de forma irregular;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(g) Manter segurança adequada em seus próprios sistemas.</Typography></li>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA SEXTA – TIPOS DE DADOS</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                6.1. Tipos de dados e categorias de titulares usualmente tratados:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">(a) Dados de identificação (nome, e-mail, telefone, empresa, cargo);</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(b) Dados técnicos (endereços IP, logs, tokens, interações com IA);</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(c) Informações inseridas pelo Cliente no uso da Plataforma.</Typography></li>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                6.2. A CONTRATANTE se responsabiliza pela inserção de dados sensíveis.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA SÉTIMA - TRANSFERÊNCIA INTERNACIONAL DE DADOS</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                7.1. A CONTRATADA não poderá transferir dados pessoais para outro país ou organização internacional sem aprovação prévia da CONTRATANTE, exceto quando necessário à execução dos serviços.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                7.2. Quando aplicável, a CONTRATADA assegurará que a transferência cumpra os requisitos da LGPD e regulamentos da ANPD.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA OITAVA – SEGURANÇA E VIOLAÇÃO</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                8.1. A CONTRATADA implementa controles de segurança como: criptografia em repouso e em trânsito, controle de acesso, backups, segregação lógica e auditorias periódicas.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                8.2. A CONTRATANTE é responsável primária por atender solicitações dos titulares. A CONTRATADA cooperará tecnicamente mediante solicitação formal.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                8.3. Em caso de violação relevante, a CONTRATADA notificará a CONTRATANTE conforme o prazo legal.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                8.4. A notificação não implica reconhecimento de culpa.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                8.5. A CONTRATADA não divulgará informações sobre a violação sem determinação legal ou da ANPD.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA NONA - MEDIDAS DE REMEDIAÇÃO E INDENIZAÇÃO</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                9.1. Em caso de violação, a CONTRATADA adotará medidas como:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                                <li><Typography variant="body2" color="text.secondary">(a) investigar causas e impactos;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(b) fornecer avisos aos titulares, quando aplicável;</Typography></li>
                                <li><Typography variant="body2" color="text.secondary">(c) adotar outras medidas proporcionais à gravidade do incidente.</Typography></li>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                9.2. A CONTRATADA responderá pelos danos causados quando descumprir obrigações legais ou instruções da CONTRATANTE, nos limites contratuais.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                9.3. Caso a CONTRATANTE seja acionada judicial, administrativa ou extrajudicialmente, a CONTRATADA prestará suporte, limitado ao valor anual do contrato.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                9.4. Cada parte responderá pelos danos diretos e comprovados decorrentes de sua culpa.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA DÉCIMA - RESCISÃO DO CONTRATO E DESTINAÇÃO DOS DADOS</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                10.1. A CONTRATADA realizará o tratamento de dados apenas para execução dos serviços contratados, incluindo coleta, armazenamento, processamento e transferência de dados necessários ao funcionamento da Plataforma.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                10.2. Após encerrada a finalidade, a CONTRATADA compromete-se a anonimizar todos os dados pessoais, salvo nas hipóteses legais de retenção, como o Marco Civil da Internet.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA DÉCIMA PRIMEIRA - ATUALIZAÇÕES E VIGÊNCIA</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                11.1. Este Acordo entra em vigor na data da adesão do Cliente aos Termos de Uso da Plataforma e permanecerá vigente enquanto houver tratamento de dados pessoais relacionado à relação contratual.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                A CONTRATADA poderá atualizar este documento para refletir alterações legais ou operacionais, publicando nova versão em seu site.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">CLÁUSULA DÉCIMA SEGUNDA - LEI APLICÁVEL E FORO</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                12.1. Aplica-se a legislação brasileira, em especial a Lei nº 13.709/2018 (LGPD).
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Fica eleito o foro da Comarca de São Paulo/SP, com renúncia a qualquer outro.
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box textAlign="center">
                        <StyledButton
                            variant="contained"
                            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                            onClick={handleBack}
                            size="large"
                        >
                            Voltar ao Cadastro
                        </StyledButton>
                    </Box>
                </StyledPaper>
            </Container>
        </BackgroundContainer>
    );
};

export default DPAPage;

